import type { IgfdPaymentPlan, LoadableResponse } from "api-types";
import {
  OrderByDirection,
  Timestamp,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { useFirebase } from "igfd-commons";

import { useMemo } from "react";
import { type UseQueryResult, useQuery, useQueryClient } from "react-query";

import { useAuthState } from "igfd-commons/src/auth";
import {
  dashboardAdminViewHistoryColRef,
  dashboardAdminViewHistoryRef,
} from "igfd-commons/src/firebase/refs";

import { useDashboardAppFeatureFlag } from "@dashboard/hooks/useFeature";
import { restaurantPaymentPlanFromDoc } from "@dashboard/store/backendApis/restaurantApi";
import type {
  FirestorePaymentPlanDocument,
  FirestoreRestaurantDocument,
} from "@dashboard/types";

import { formatCreatedAt } from "./shell/workspaces/workspaceDates";

export type RawRestDetails = {
  restaurant: FirestoreRestaurantDocument;
  paymentPlan: IgfdPaymentPlan;
  isRecentlyViewed: boolean;
};

type ViewAdminRestaurant = {
  viewData: LoadableResponse<RawRestDetails[]>;

  // True when the underlying restaurants query failed, so callers can show an
  // error state instead of an empty "no workspaces" list.
  error: boolean;

  // Save the view to update the ordering later on.
  onView: (restaurantUuid: string) => Promise<void>;
};

/**
 * Hook to get all restaurants from firestore.
 * Also allows for saving the view history to access the documents in the order they were viewed.
 *
 * View History works by storing admin-users/{uid}/dashboard-view-history/{restaurantUuid} documents
 * the document contains a timestamp field that is updated every time the restaurant is viewed.
 */
export const useViewAdminRestaurants = () => {
  const { firestoreRefs } = useFirebase();
  const { user } = useAuthState();

  const saveViewHistory = (restaurantUuid: string) => {
    if (!user) return Promise.resolve();
    const uid = user.uid;
    const viewHistoryRef = dashboardAdminViewHistoryRef(
      firestoreRefs.firestore,
      uid,
      restaurantUuid
    );
    return setDoc(viewHistoryRef, { timestamp: Timestamp.now() });
  };

  const getViewHistory = () => {
    if (!user) return Promise.resolve([]);
    const uid = user.uid;
    return getDocs(
      query(
        dashboardAdminViewHistoryColRef(firestoreRefs.firestore, uid),
        orderBy("timestamp", "desc" as OrderByDirection)
      )
    ).then((querySnapshot) => {
      const restaurantUuids = querySnapshot.docs.map((doc) => doc.id);
      return restaurantUuids;
    });
  };

  const fetchRestaurantsData = async () => {
    const [querySnapshot, recentlyViewedRestaurantUuids, paymentPlans] =
      await Promise.all([
        getDocs(firestoreRefs.restaurants()),
        getViewHistory(),
        getDocs(firestoreRefs.paymentPlans()),
      ]);

    const restaurants = querySnapshot.docs.map(
      (doc) => doc.data() as FirestoreRestaurantDocument
    );

    // recently viewed restaurants should be at the top of the list
    // then sort by createdAt descending, if the field is not defined, we will sort by name
    const restaurantsRecentlyViewed = restaurants
      .filter((restaurant) =>
        recentlyViewedRestaurantUuids.includes(restaurant.uuid)
      )
      .sort((a, b) => {
        const aIndex = recentlyViewedRestaurantUuids.indexOf(a.uuid);
        const bIndex = recentlyViewedRestaurantUuids.indexOf(b.uuid);
        return aIndex - bIndex;
      });

    const others = restaurants.filter(
      (restaurant) => !recentlyViewedRestaurantUuids.includes(restaurant.uuid)
    );

    const restaurantsWithCreatedAt = others.filter(
      (restaurant) => restaurant.createdAt
    );
    const restaurantsWithoutCreatedAt = others.filter(
      (restaurant) => !restaurant.createdAt
    );

    const sortedRestaurants = restaurantsWithCreatedAt.sort(
      (a, b) => b.createdAt - a.createdAt
    );
    const unsortedRestaurants = restaurantsWithoutCreatedAt.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const orderedRestaurants = [
      ...restaurantsRecentlyViewed,
      ...sortedRestaurants,
      ...unsortedRestaurants,
    ];

    const restaurantsWithPaymentPlan = orderedRestaurants.map((restaurant) => {
      // A payment plan doc is created transactionally with every restaurant,
      // so the match is guaranteed to exist.
      const paymentPlanDoc = paymentPlans.docs
        .find((doc) => doc.id === restaurant.uuid)!
        .data() as FirestorePaymentPlanDocument;
      const paymentPlan = restaurantPaymentPlanFromDoc(paymentPlanDoc);
      return {
        restaurant,
        paymentPlan,
        isRecentlyViewed: recentlyViewedRestaurantUuids.includes(
          restaurant.uuid
        ),
      } as RawRestDetails;
    });

    return restaurantsWithPaymentPlan;
  };

  const { data, isLoading, isError } = useQuery(
    ["admin-restaurants", user?.uid],
    fetchRestaurantsData,
    {
      enabled: !!user,
    }
  );

  // Redux strips env, so this admin list is the only place it can be filtered.
  const showDemoRestaurantsOnly = useDashboardAppFeatureFlag(
    "showDemoRestaurantsOnly"
  );
  const visibleData = useMemo(() => {
    const restaurants = data || [];
    return showDemoRestaurantsOnly
      ? restaurants.filter(({ restaurant }) => restaurant.env === "demo")
      : restaurants;
  }, [data, showDemoRestaurantsOnly]);

  return {
    viewData: {
      isLoading,
      data: visibleData,
    } as LoadableResponse<RawRestDetails[]>,
    error: isError,
    onView: saveViewHistory,
  } as ViewAdminRestaurant;
};

// The acceptance record — restaurants-v1/{uuid}/restaurantLegal/{uuid} — exists
// only once a workspace has accepted, and every writer stamps createdAt, so the
// date is present exactly when accepted. A union makes an accepted-without-date
// state unrepresentable.
export type RestaurantTermsAcceptance =
  | { accepted: false }
  | { accepted: true; createdAt: string };

export const restaurantTermsAcceptanceKey = (
  restaurantUuid: string | undefined
) => ["restaurant-terms-acceptance", restaurantUuid];

/**
 * Reads the single restaurantLegal doc for a workspace to show whether, and
 * when, the restaurant accepted the terms and conditions. The date is formatted
 * here so callers render a ready string.
 */
export const useRestaurantTermsAcceptance = (
  restaurantUuid: string | undefined
): UseQueryResult<RestaurantTermsAcceptance> => {
  const { firestoreRefs } = useFirebase();

  return useQuery(
    restaurantTermsAcceptanceKey(restaurantUuid),
    async (): Promise<RestaurantTermsAcceptance> => {
      const snapshot = await getDoc(
        firestoreRefs.restaurantTermsAcceptance(restaurantUuid!)
      );
      if (!snapshot.exists()) {
        return { accepted: false };
      }
      const { createdAt } = snapshot.data() as { createdAt: Timestamp };
      return { accepted: true, createdAt: formatCreatedAt(createdAt) };
    },
    { enabled: restaurantUuid !== undefined }
  );
};

export const useInvalidateAdminRestaurants = () => {
  const { user } = useAuthState();
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries(["admin-restaurants", user?.uid]);
  };
};
